import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../../store";
import type { MapSaveStatusState, MapSaveStatusEntry } from "./mapSaveStatusTypes";

const initialState: MapSaveStatusState = {};

const mapSaveStatusSlice = createSlice({
  name: "mapSaveStatus",
  initialState,
  reducers: {
    /**
     * Start polling for map save status. Sets status to in_progress.
     * Idempotent for the same project (no-op if already in_progress).
     */
    startMapSavePolling: (state, action: PayloadAction<{ projectId: string }>) => {
      const { projectId } = action.payload;
      const existing = state[projectId];

      if (existing?.status === "in_progress") {
        return;
      }

      state[projectId] = {
        status: "in_progress",
        startedAt: Date.now(),
      };
    },

    /**
     * Update map save status. Used when polling returns SAVE_SUCCESS, SAVE_ERROR, or error object.
     */
    updateMapSaveStatus: (
      state,
      action: PayloadAction<{
        projectId: string;
        status: MapSaveStatusEntry["status"];
        errorMessage?: string;
      }>
    ) => {
      const { projectId, status, errorMessage } = action.payload;

      if (!state[projectId]) {
        state[projectId] = { status, startedAt: Date.now() };
      } else {
        state[projectId].status = status;
      }

      if (errorMessage !== undefined) {
        state[projectId].errorMessage = errorMessage;
      }
    },

    /**
     * Clear status for a project. Resets to idle.
     */
    clearMapSaveStatus: (state, action: PayloadAction<{ projectId: string }>) => {
      const { projectId } = action.payload;
      delete state[projectId];
    },

    /**
     * Clear all map save status. For cleanup (e.g. on logout).
     */
    clearAllMapSaveStatus: () => {
      return initialState;
    },
  },
});

export const {
  startMapSavePolling,
  updateMapSaveStatus,
  clearMapSaveStatus,
  clearAllMapSaveStatus,
} = mapSaveStatusSlice.actions;

export const selectMapSaveStatus = (
  state: RootState,
  projectId: string
): MapSaveStatusEntry | null => {
  return state.mapSaveStatus[projectId] ?? null;
};

export const selectIsMapSaveInProgress = (
  state: RootState,
  projectId: string
): boolean => {
  return state.mapSaveStatus[projectId]?.status === "in_progress";
};

export interface MapSaveStatusMessage {
  text: string;
  type: "success" | "error" | "info";
  color: string;
  backgroundColor: string;
  borderColor: string;
}

export const selectMapSaveStatusMessage = (
  state: RootState,
  projectId: string
): MapSaveStatusMessage | null => {
  const entry = state.mapSaveStatus[projectId];
  if (!entry || entry.status === "idle") {
    return null;
  }

  switch (entry.status) {
    case "in_progress":
      return {
        text: "Save in progress...",
        type: "info",
        color: "var(--bintact-bright)",
        backgroundColor: "#e3f2fd",
        borderColor: "var(--bintact-bright)",
      };
    case "success":
      return {
        text: "Map saved successfully",
        type: "success",
        color: "var(--bintact-bright)",
        backgroundColor: "#e8f5e9",
        borderColor: "var(--bintact-bright)",
      };
    case "error":
      return {
        text: entry.errorMessage ?? "An error occurred while saving the map",
        type: "error",
        color: "var(--error)",
        backgroundColor: "#ffebee",
        borderColor: "var(--error)",
      };
    default:
      return null;
  }
};

export const selectProjectsWithSaveInProgress = (
  state: RootState
): string[] => {
  return Object.entries(state.mapSaveStatus)
    .filter(([, entry]) => entry.status === "in_progress")
    .map(([projectId]) => projectId);
};

export default mapSaveStatusSlice.reducer;
